import * as React from 'react';
import { useAuth } from 'auth';
import { useGetTranslations } from 'api/translations';
import { BaseLayout } from 'layout';
import { createTranslator, I18nContext, Translator } from './i18n-context';

export type { I18nContextShape, Translator } from './i18n-context';
export { I18nContext } from './i18n-context';

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { data: translations } = useGetTranslations();
    const { data: translationsEn } = useGetTranslations('en');
    const auth = useAuth();

    // the design system reads this same context (via `useTranslations`), so DS copy comes from this app's
    // CSV and Phrase project like every other string in the product. Memoised on the loaded locales:
    // `__` is a dep of memos all over the app (table column sets included, where a new identity
    // remounts every cell), so it must not change identity on unrelated provider renders.
    const __ = React.useMemo<Translator>(() => {
        const translate = createTranslator(translations);
        const translateEn = createTranslator(translationsEn);

        return (key, tokens, forceEnglish) => (forceEnglish ? translateEn(key, tokens) : translate(key, tokens));
    }, [translations, translationsEn]);
    const contextValue = React.useMemo(() => ({ __ }), [__]);

    return (
        <I18nContext.Provider value={contextValue}>
            {!auth.user || translations ? children : <BaseLayout isLoading />}
        </I18nContext.Provider>
    );
};
